import { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { alertsOperations } from '../../../../../../../../../ducks/alerts';
import { fetchDotListWithLabData } from '../utils/dataFetching';

/**
 * Custom React hook to fetch and manage DOT list, selected DOT, year, and testing authority state.
 *
 * @param {string} legacyClientId - The legacy client ID for the selected site. (ERID)
 * @param {string} currentUserId - The current user object ID.
 * @returns {object} - State and setters for DOT list, selected DOT, year, and testing authority.
 *   {
 *     dotList: Array<{ value: string, label: string, ... }>,
 *     selectedDOT: object|null,
 *     setSelectedDOT: function,
 *     selectedYear: string|null,
 *     setSelectedYear: function,
 *     selectedTestingAuthority: object|null,
 *     setSelectedTestingAuthority: function,
 *     testingAuthorityOptions: Array<{ value: number, label: string }>
 *   }
 */
export const useDotList = (legacyClientId, currentUserId) => {
  const dispatch = useDispatch();
  const [dotList, setDotList] = useState([]);
  const [selectedDOT, setSelectedDOT] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTestingAuthority, setSelectedTestingAuthority] = useState(null);

  useEffect(() => {
    fetchDotListWithLabData(legacyClientId, currentUserId)
      .then((list) => {
        setDotList(list);
        setSelectedDOT(null);
        setSelectedYear(null);
        setSelectedTestingAuthority(null);
      })
      .catch(() => {
        dispatch(alertsOperations.addAlert('Error fetching DOT numbers. Please try again.', 'danger'));
      });
  }, [legacyClientId, currentUserId, dispatch]);

  const testingAuthorityOptions = useMemo(() => {
    if (!selectedDOT?.testingAuthorities?.length) return [];
    const map = new Map();
    selectedDOT.testingAuthorities.forEach(auth => {
      if (auth?.testingAuthorityId && !map.has(auth.testingAuthorityId)) {
        map.set(auth.testingAuthorityId, {
          value: auth.testingAuthorityId,
          label: auth.testingAuthority,
        });
      }
    });
    return Array.from(map.values());
  }, [selectedDOT]);

  const dotOptions = useMemo(
    () => dotList.map(dot => ({
      value: dot.DOTNumber,
      label: dot.DOTNumber,
      ...dot,
    })),
    [dotList]
  );

  return {
    dotList: dotOptions,
    selectedDOT,
    setSelectedDOT: dot => {
      setSelectedDOT(dot);
      setSelectedTestingAuthority(null);
    },
    selectedYear,
    setSelectedYear,
    selectedTestingAuthority,
    setSelectedTestingAuthority,
    testingAuthorityOptions,
  };
};